import React, { useState, useEffect, useRef } from 'react';
import '../common/SharedStyles.css';
import WordInfo from './WordInfo';

// Define the interface for a single definition
interface Definition {
  text: string;
  partOfSpeech?: string;
  examples: string[];
}

interface WordItemProps {
  item: {
    _id?: string;
    word: string;
    definition: string;
    definitions?: Definition[]; // New field for multiple definitions
    phonetics?: string;
    phonics?: string;
    examples?: string[];
    tags?: string[];
    createdAt?: Date | string;
    lastSearched?: Date | string;
    searchCount?: number;
    source?: 'openai' | 'fallback';
  };
  isExpanded?: boolean;
  onToggleExpand?: (id: string, event: React.MouseEvent) => void;
  onRemove?: (id: string, event: React.MouseEvent) => void;
  onClick?: (word: string) => void; // This is now optional and will only be used for specific buttons
  formatSubtitle?: (item: any) => string;
  className?: string;
  onRefresh?: () => void; // Optional callback for when refresh completes
}

const WordItem: React.FC<WordItemProps> = ({
  item,
  isExpanded = false,
  onToggleExpand,
  onRemove,
  onClick,
  formatSubtitle,
  className = '',
  onRefresh
}) => {
  // Local expand state if no external control is provided
  const [localExpanded, setLocalExpanded] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [wordData, setWordData] = useState(item);
  const refreshRequestedRef = useRef(false);
  
  // Store the pre-refresh expanded state
  const [wasExpanded, setWasExpanded] = useState(false);
  
  // Add flag to track if this is an initial auto-load vs explicit refresh
  const [isInitialAutoLoad, setIsInitialAutoLoad] = useState(false);
  
  // Determine if the item is expanded (controlled or uncontrolled)
  // During refresh, force it to be expanded regardless of other states
  const expanded = refreshing ? true : (onToggleExpand ? isExpanded : localExpanded);
  
  // Effect to track expanded state for use during refresh
  useEffect(() => {
    if (expanded && !wasExpanded) {
      setWasExpanded(true);
    }
  }, [expanded, wasExpanded]);
  
  // Check if definition data is missing or incomplete
  const isDefinitionMissing = () => {
    return (
      !wordData.definition || 
      (wordData.definition.includes("not available") || wordData.definition.includes("No definition available")) ||
      (!wordData.definitions || wordData.definitions.length === 0)
    );
  };
  
  // Effect to check and fetch definition data when expanded
  useEffect(() => {
    // Only fetch if:
    // 1. The card is expanded
    // 2. The definition is missing
    // 3. We haven't already tried fetching (refreshing is false)
    if (expanded && isDefinitionMissing() && !refreshing) {
      console.log(`Auto-fetching missing definition for "${wordData.word}"`);
      // Set flag for initial load - this will prevent the loading spinner
      setIsInitialAutoLoad(true);
      fetchDefinitionFromDB();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expanded]); // Only re-run when expanded state changes
  
  // Update internal wordData when item prop changes
  useEffect(() => {
    setWordData(item);
  }, [item]);
  
  // Handle toggle expand - both for button and card click
  const handleToggleExpand = (event: React.MouseEvent) => {
    // Skip toggling if currently refreshing
    if (refreshing) {
      console.log(`Ignoring toggle during refresh for "${wordData.word}"`);
      event.stopPropagation();
      return;
    }
    
    event.stopPropagation();
    if (onToggleExpand && item._id) {
      onToggleExpand(item._id, event);
    } else {
      setLocalExpanded(!localExpanded);
    }
  };
  
  // Handle remove
  const handleRemove = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onRemove && item._id) {
      onRemove(item._id, event);
    }
  };
  
  // Handle word title click - now used only by specific buttons if needed
  const handleWordClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (onClick) {
      onClick(item.word);
    }
  };
  
  // First try to get definition from DB without forcing API call
  const fetchDefinitionFromDB = async () => {
    if (!wordData.word || refreshing) return;
    
    // Only set refreshing if this is not an initial auto-load
    if (!isInitialAutoLoad) {
      setRefreshing(true);
    }
    refreshRequestedRef.current = true;
    
    // Store pre-refresh expanded state
    setWasExpanded(expanded);
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        setRefreshing(false);
        refreshRequestedRef.current = false;
        setIsInitialAutoLoad(false);
        return;
      }
      
      // Try to get definition from DB first (without forceApi parameter)
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(wordData.word)}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get definition');
      }
      
      const data = await response.json();
      
      // Check if we still have missing data after DB fetch
      const stillMissingData = !data.definition || 
        (data.definition.includes("not available") || data.definition.includes("No definition available")) ||
        (!data.definitions || data.definitions.length === 0);
      
      if (stillMissingData) {
        // If still missing data after DB fetch, try from OpenAI
        console.log(`Database definition missing for "${wordData.word}", fetching from OpenAI`);
        // Reset auto-load flag since we're doing an API fetch
        setIsInitialAutoLoad(false);
        await handleRefreshDefinition();
        return;
      }
      
      // Update word data with definition from DB
      setWordData({
        ...wordData,
        definition: data.definition,
        definitions: data.definitions,
        phonetics: data.phonetics,
        phonics: data.phonics,
        examples: data.examples,
        source: data.source
      });
      
      // The server-side /api/search/define endpoint already updates the search count
      // and saves the definition, so we don't need to make another API call here
      
      // Only trigger refresh callback if this was a significant update
      // like fetching from OpenAI, not just retrieving from DB
      const isFromOpenAI = data.source === 'openai' && data.metadata && !data.metadata.fromDatabase;
      if (onRefresh && isFromOpenAI && refreshRequestedRef.current) {
        console.log(`Definition updated from OpenAI for "${wordData.word}", triggering refresh`);
        setTimeout(() => {
          onRefresh();
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching definition from database:', error);
      // If DB fetch fails, try the OpenAI fetch as fallback
      setIsInitialAutoLoad(false);
      handleRefreshDefinition();
    } finally {
      setTimeout(() => {
        setRefreshing(false);
        refreshRequestedRef.current = false;
        setIsInitialAutoLoad(false);
      }, 600); // Longer delay to ensure the UI has time to update
    }
  };
  
  // Handle refresh definition - directly from OpenAI
  const handleRefreshDefinition = async () => {
    if (!wordData.word || (refreshing && !refreshRequestedRef.current)) return;
    
    try {
      setRefreshing(true);
      setIsInitialAutoLoad(false); // Ensure this is not considered an auto-load
      refreshRequestedRef.current = true;
      
      // Store pre-refresh expanded state
      setWasExpanded(expanded);
      console.log(`Refreshing "${wordData.word}" - forcing expanded state during refresh`);
      
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token available');
        setRefreshing(false);
        refreshRequestedRef.current = false;
        return;
      }
      
      // Force fetch a new definition from OpenAI
      const response = await fetch(`/api/search/define?word=${encodeURIComponent(wordData.word)}&forceApi=true`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to get updated definition');
      }
      
      const data = await response.json();
      
      // The server already saves the definition when using forceApi=true
      // so no need for a separate POST request here
      
      // Update local state with new data first
      setWordData({
        ...wordData,
        definition: data.definition,
        definitions: data.definitions,
        phonetics: data.phonetics,
        phonics: data.phonics,
        examples: data.examples,
        source: data.source
      });
      
      // We know this was a refresh from OpenAI, so we can trigger the refresh callback
      if (onRefresh && refreshRequestedRef.current) {
        // Wrap in setTimeout to avoid immediate React state update cycles
        setTimeout(() => {
          console.log(`Forced refresh of definition for "${wordData.word}" complete - keeping expanded`);
          onRefresh();
        }, 300);
      }
    } catch (error) {
      console.error('Error refreshing definition:', error);
      alert('Failed to refresh definition. Please try again.');
    } finally {
      // Delay turning off the refreshing state to give time for the UI to update
      setTimeout(() => {
        setRefreshing(false);
        refreshRequestedRef.current = false;
      }, 600);
    }
  };
  
  // Default subtitle formatter
  const defaultFormatSubtitle = (item: any) => {
    const count = (item.searchCount || 0) > 1 ? `${item.searchCount} times` : 'once';
    const date = new Date(item.lastSearched || item.createdAt).toLocaleDateString();
    return `Searched ${count} · Last: ${date}`;
  };
  
  // Use provided formatter or default
  const subtitleFormatter = formatSubtitle || defaultFormatSubtitle;
  
  // Determine which CSS classes to use
  const containerClass = `item-card word-item ${className} ${expanded ? 'expanded' : ''}`;
  
  return (
    <li
      className={containerClass}
      onClick={handleToggleExpand} // Changed to use handleToggleExpand instead of handleClick
    >
      <div className="item-card-header">
        <h3 className="item-title">
          {onClick ? (
            <span className="clickable-title" onClick={handleWordClick}>{wordData.word}</span>
          ) : (
            wordData.word
          )}
        </h3>
        <div className="word-meta-container">
          <span className="item-subtitle">{subtitleFormatter(wordData)}</span>
          <div className="word-actions">
            {onToggleExpand && (
              <button 
                className="toggle-button"
                onClick={handleToggleExpand}
                disabled={refreshing} // Disable toggle button during refresh
              >
                {expanded ? 'Hide details' : 'Show details'}
              </button>
            )}
            {onRemove && (
              <button 
                className="remove-button"
                onClick={handleRemove}
                title="Remove word"
                disabled={refreshing} // Disable remove button during refresh
              >
                Remove
              </button>
            )}
          </div>
        </div>
      </div>
      
      {/* Always render the details container when expanded OR refreshing */}
      {(expanded || refreshing) && (
        <div className="item-details" style={{ position: 'relative' }}>
          {/* Always show the content, but overlay it with a spinner when refreshing */}
          <WordInfo
            definition={wordData.definition}
            definitions={wordData.definitions}
            phonetics={wordData.phonetics}
            phonics={wordData.phonics}
            examples={wordData.examples}
            source={wordData.source}
            tags={wordData.tags}
            onRefresh={handleRefreshDefinition}
            refreshing={refreshing && !isInitialAutoLoad}
          />
          
          {/* Show loading spinner overlay only when refreshing AND NOT during initial auto-load */}
          {refreshing && !isInitialAutoLoad && (
            <div className="loading-spinner-overlay">
              <div className="loading-spinner">Updating definition...</div>
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export default WordItem; 